// Auto-extracted from Profile.jsx
import { useState, useEffect } from "react";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { supabase } from "../../lib/supabase";
import { Field, inputStyle } from "./shared";

// =============================================================================
// SELLER PAYOUT METHODS (PIN-gated)
// =============================================================================

// SHA-256(pin + userId) -> hex. Mirrors mobile usePayoutMethods so the same PIN
// row works across clients.
async function hashPin(pin, userId) {
  const enc = new TextEncoder().encode(`${pin}${userId}`);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const PAYOUT_NETWORKS = [
  { id: "mtn", label: "MTN MoMo" },
  { id: "airtel", label: "Airtel Money" },
  { id: "zamtel", label: "Zamtel Money" },
];

function PinPad({ value, onChange, autoFocus }) {
  return (
    <input
      type="password"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={4}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
      autoFocus={autoFocus}
      style={{ ...inputStyle, textAlign: "center", fontSize: 22, letterSpacing: 10, fontWeight: 800 }}
      placeholder="••••"
    />
  );
}

export function SellerPayoutMethodsPanel({ user }) {
  // PIN state machine: loading → setup (no PIN exists) → confirm → unlocked.
  //                     verify (PIN exists) → unlocked.
  const [pinStep, setPinStep] = useState("loading");
  const [pinRow, setPinRow] = useState(null);
  const [pinA, setPinA] = useState("");
  const [pinB, setPinB] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [pinMsg, setPinMsg] = useState(null);

  const [methods, setMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  // Add flow: picker → number → otp → done
  const [addStep, setAddStep] = useState(null);
  const [addForm, setAddForm] = useState({ network: "mtn", number: "", otp: "" });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addMsg, setAddMsg] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    async function loadPin() {
      const { data } = await supabase
        .from("payment_pins")
        .select("user_id, pin_hash, failed_attempts, locked_until")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setPinRow(data || null);
      if (!data) {
        setPinStep("setup");
      } else if (data.locked_until && new Date(data.locked_until) > new Date()) {
        setPinStep("locked");
      } else {
        setPinStep("verify");
      }
    }
    loadPin();
    return () => { cancelled = true; };
  }, [user?.id]);

  async function loadMethods() {
    if (!user?.id) return;
    setLoadingMethods(true);
    const { data } = await supabase
      .from("payment_methods")
      .select("id, network, masked_number, is_default, is_verified, created_at")
      .eq("user_id", user.id)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: false });
    setMethods(data || []);
    setLoadingMethods(false);
  }

  async function submitSetup() {
    if (pinA.length !== 4) return setPinMsg({ tone: "red", text: "PIN must be 4 digits." });
    setPinMsg(null);
    setPinStep("confirm");
  }

  async function submitConfirm() {
    if (pinA !== pinB) {
      setPinMsg({ tone: "red", text: "PINs don't match. Try again." });
      setPinB("");
      return;
    }
    setPinMsg(null);
    const hash = await hashPin(pinA, user.id);
    const { error } = await supabase.from("payment_pins").upsert({
      user_id: user.id,
      pin_hash: hash,
      failed_attempts: 0,
      locked_until: null,
    }, { onConflict: "user_id" });
    if (error) return setPinMsg({ tone: "red", text: error.message });
    setPinA(""); setPinB("");
    setPinStep("unlocked");
    loadMethods();
  }

  async function submitVerify() {
    if (pinA.length !== 4) return setPinMsg({ tone: "red", text: "Enter your 4-digit PIN." });
    setPinMsg(null);
    const hash = await hashPin(pinA, user.id);
    if (hash === pinRow?.pin_hash) {
      await supabase.from("payment_pins").update({ failed_attempts: 0, locked_until: null }).eq("user_id", user.id);
      setPinA("");
      setAttempts(0);
      setPinStep("unlocked");
      loadMethods();
      return;
    }
    const nextAttempts = (pinRow?.failed_attempts || 0) + 1;
    const lock = nextAttempts >= 5;
    const lockedUntil = lock ? new Date(Date.now() + 15 * 60000).toISOString() : null;
    await supabase.from("payment_pins").update({
      failed_attempts: lock ? 0 : nextAttempts,
      locked_until: lockedUntil,
    }).eq("user_id", user.id);
    setPinA("");
    if (lock) {
      setPinStep("locked");
      setPinRow({ ...pinRow, locked_until: lockedUntil });
    } else {
      setAttempts(nextAttempts);
      setPinMsg({ tone: "red", text: `Wrong PIN. ${5 - nextAttempts} attempt${5 - nextAttempts === 1 ? "" : "s"} left.` });
      setPinRow({ ...pinRow, failed_attempts: nextAttempts });
    }
  }

  async function addMethod() {
    const digits = addForm.number.replace(/\D/g, "");
    if (digits.length < 9) { setAddMsg({ tone: "red", text: "Enter a valid phone number." }); return; }
    setAddMsg(null);
    setAddStep("otp");
  }

  async function confirmOtp() {
    if (addForm.otp.length !== 6) { setAddMsg({ tone: "red", text: "Enter the 6-digit code." }); return; }
    // OTP is simulated in dev (any 6 digits works). When SMS provider wired in,
    // compare to the code stored by the `send-payout-otp` Edge Function.
    setAddSubmitting(true);
    setAddMsg(null);
    const digits = addForm.number.replace(/\D/g, "");
    const masked = `${digits.slice(0, 3)}****${digits.slice(-3)}`;
    const { error } = await supabase.from("payment_methods").insert({
      user_id: user.id,
      type: "mobile_money",
      network: addForm.network,
      masked_number: masked,
      phone_number: digits,
      is_default: methods.length === 0,
      is_verified: true,
      verified_at: new Date().toISOString(),
    });
    setAddSubmitting(false);
    if (error) { setAddMsg({ tone: "red", text: error.message }); return; }
    setAddForm({ network: "mtn", number: "", otp: "" });
    setAddStep(null);
    loadMethods();
  }

  async function remove(id) {
    if (!confirm("Remove this payout method?")) return;
    await supabase.from("payment_methods").delete().eq("id", id);
    setMethods((prev) => prev.filter((m) => m.id !== id));
  }

  async function setDefault(id) {
    await supabase.from("payment_methods").update({ is_default: false }).eq("user_id", user.id);
    await supabase.from("payment_methods").update({ is_default: true }).eq("id", id);
    setMethods((prev) => prev.map((m) => ({ ...m, is_default: m.id === id })));
  }

  // --- Render gate states ---

  if (pinStep === "loading") {
    return <Skeleton h={120} />;
  }

  if (pinStep === "locked") {
    const unlocksAt = pinRow?.locked_until ? new Date(pinRow.locked_until).toLocaleTimeString() : "soon";
    return (
      <Card pad={22}>
        <div style={{ textAlign: "center", padding: "20px 10px" }}>
          <span style={{ width: 52, height: 52, borderRadius: 16, background: C.redBg, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="lock" size={22} color={C.red} />
          </span>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.charcoal, marginTop: 14 }}>PIN locked</div>
          <div style={{ fontSize: 13, color: C.slate, marginTop: 6 }}>
            Too many wrong attempts. Try again after {unlocksAt}.
          </div>
        </div>
      </Card>
    );
  }

  if (pinStep === "setup" || pinStep === "confirm") {
    const isConfirm = pinStep === "confirm";
    return (
      <Card pad={22}>
        <Eyebrow>Secure your payout methods</Eyebrow>
        <div style={{ fontSize: 13, color: C.slate, marginTop: 8, lineHeight: 1.6 }}>
          {isConfirm
            ? "Re-enter the same PIN to confirm. You'll use this PIN whenever you add or change a payout method."
            : "Set a 4-digit PIN that protects your payout numbers. You'll enter it each time you open this page."}
        </div>
        <div style={{ marginTop: 20, maxWidth: 280 }}>
          {isConfirm ? (
            <PinPad value={pinB} onChange={setPinB} autoFocus />
          ) : (
            <PinPad value={pinA} onChange={setPinA} autoFocus />
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
          {isConfirm ? (
            <>
              <Btn onClick={submitConfirm} disabled={pinB.length !== 4}>Set PIN</Btn>
              <Btn kind="outline" size="sm" onClick={() => { setPinStep("setup"); setPinB(""); setPinMsg(null); }}>Back</Btn>
            </>
          ) : (
            <Btn onClick={submitSetup} disabled={pinA.length !== 4}>Continue</Btn>
          )}
          {pinMsg && <span style={{ fontSize: 12, fontWeight: 700, color: pinMsg.tone === "red" ? C.red : C.greenDk }}>{pinMsg.text}</span>}
        </div>
      </Card>
    );
  }

  if (pinStep === "verify") {
    return (
      <Card pad={22}>
        <Eyebrow>Enter your PIN</Eyebrow>
        <div style={{ fontSize: 13, color: C.slate, marginTop: 8 }}>
          Enter your 4-digit PIN to view your payout methods.
        </div>
        <div style={{ marginTop: 20, maxWidth: 280 }}>
          <PinPad value={pinA} onChange={setPinA} autoFocus />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
          <Btn onClick={submitVerify} disabled={pinA.length !== 4}>Unlock</Btn>
          {pinMsg && <span style={{ fontSize: 12, fontWeight: 700, color: pinMsg.tone === "red" ? C.red : C.greenDk }}>{pinMsg.text}</span>}
          {attempts > 0 && !pinMsg && <span style={{ fontSize: 12, color: C.muted }}>{5 - attempts} attempts remaining</span>}
        </div>
      </Card>
    );
  }

  // --- Unlocked: methods list + add flow ---
  const networkLabel = { mtn: "MTN MoMo", airtel: "Airtel Money", zamtel: "Zamtel Money" };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={22}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <Eyebrow>Payout methods</Eyebrow>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              Mobile money numbers where Kapio sends your payouts.
            </div>
          </div>
          {addStep === null && <Btn onClick={() => { setAddStep("picker"); setAddMsg(null); }} icon="plus">Add number</Btn>}
        </div>
      </Card>

      {addStep === "picker" && (
        <Card pad={22}>
          <Eyebrow>Add payout number</Eyebrow>
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <Field label="Network">
              <select value={addForm.network} onChange={(e) => setAddForm({ ...addForm, network: e.target.value })} style={inputStyle}>
                {PAYOUT_NETWORKS.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
              </select>
            </Field>
            <Field label="Phone number">
              <input
                type="tel"
                value={addForm.number}
                onChange={(e) => setAddForm({ ...addForm, number: e.target.value })}
                placeholder="0971234567"
                style={inputStyle}
              />
            </Field>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Btn onClick={addMethod}>Send verification code</Btn>
              <Btn kind="outline" size="sm" onClick={() => { setAddStep(null); setAddMsg(null); }}>Cancel</Btn>
              {addMsg && <span style={{ fontSize: 12, fontWeight: 700, color: addMsg.tone === "red" ? C.red : C.greenDk }}>{addMsg.text}</span>}
            </div>
          </div>
        </Card>
      )}

      {addStep === "otp" && (
        <Card pad={22}>
          <Eyebrow>Verify number</Eyebrow>
          <div style={{ fontSize: 12.5, color: C.slate, marginTop: 8, lineHeight: 1.5 }}>
            We sent a 6-digit code to <b>{addForm.number}</b>. Enter it to verify.
          </div>
          <div style={{ marginTop: 14, maxWidth: 280 }}>
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={addForm.otp}
              onChange={(e) => setAddForm({ ...addForm, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              placeholder="000000"
              style={{ ...inputStyle, textAlign: "center", fontSize: 22, letterSpacing: 10, fontWeight: 800 }}
              autoFocus
            />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
            <Btn onClick={confirmOtp} disabled={addSubmitting || addForm.otp.length !== 6}>
              {addSubmitting ? "Verifying…" : "Verify & add"}
            </Btn>
            <Btn kind="outline" size="sm" onClick={() => setAddStep("picker")}>Back</Btn>
            {addMsg && <span style={{ fontSize: 12, fontWeight: 700, color: addMsg.tone === "red" ? C.red : C.greenDk }}>{addMsg.text}</span>}
          </div>
        </Card>
      )}

      <Card pad={22}>
        {loadingMethods ? <Skeleton h={60} /> : methods.length === 0 ? (
          <div style={{ padding: "16px 0", fontSize: 12.5, color: C.muted }}>No payout numbers yet. Add one to start receiving payouts.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {methods.map((m) => (
              <div key={m.id} style={{
                padding: 14, borderRadius: 12,
                border: m.is_default ? `2px solid ${C.hunter}` : "1px solid rgba(196,193,183,0.35)",
                background: m.is_default ? C.cream : C.white,
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <span style={{ width: 40, height: 40, borderRadius: 10, background: C.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="smartphone" size={16} color={C.hunter} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal, display: "flex", alignItems: "center", gap: 8 }}>
                    {networkLabel[m.network] || m.network}
                    {m.is_default && <span style={{ fontSize: 9.5, padding: "2px 6px", borderRadius: 6, background: C.hunter, color: C.dust, fontWeight: 800, letterSpacing: 0.5 }}>DEFAULT</span>}
                    {m.is_verified && <span style={{ fontSize: 9.5, padding: "2px 6px", borderRadius: 6, background: C.greenBg, color: C.greenDk, fontWeight: 800, letterSpacing: 0.5 }}>VERIFIED</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{m.masked_number || "—"}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!m.is_default && <Btn size="sm" kind="outline" onClick={() => setDefault(m.id)}>Default</Btn>}
                  <button onClick={() => remove(m.id)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
                    <Icon name="trash-2" size={14} color={C.red} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
